import { CdkStepperModule } from '@angular/cdk/stepper';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, ViewChild, inject } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  UppyAngularDashboardModalModule,
  UppyAngularDashboardModule,
  UppyAngularDragDropModule,
  UppyAngularProgressBarModule,
  UppyAngularStatusBarModule,
} from '@uppy/angular';
import axios from 'axios';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import { ChannelService } from '../../../../services/channel.service';
import * as channelActions from '../../../../state/actions/channel.actions';
import { AppState } from '../../../../state/appstate';
import { BtnActionComponent } from '../../../components/buttons/btn-action/btn-action.component';
import { LoadingDotsComponent } from '../../../components/spinners/loading-dots/loading-dots.component';
import { ChannelTncComponent } from '../../../legal/channel-tnc/channel-tnc.component';
import { NamingGuidelinesComponent } from '../../../legal/naming-guidelines/naming-guidelines.component';

@Component({
  selector: 'app-create-channel',
  templateUrl: './create-channel.component.html',
  standalone: true,
  imports: [
    RouterLink,
    CommonModule,
    ReactiveFormsModule,
    LoadingDotsComponent,
    MatStepperModule,
    MatDialogModule,
    CdkStepperModule,
    UppyAngularDashboardModule,
    UppyAngularDashboardModalModule,
    UppyAngularDragDropModule,
    UppyAngularProgressBarModule,
    UppyAngularStatusBarModule,
    BtnActionComponent,
  ],
})
export class CreateChannelComponent {
  @ViewChild('stepper') stepper!: MatStepper;

  readonly dialog = inject(MatDialog);

  channelNameFormGroup: FormGroup;
  acceptTermsFormGroup: FormGroup;

  tags: Array<string> = [];

  working$: Observable<boolean>;

  checkingAvailability: boolean = false;

  user$: Subscription | undefined;

  private ipApiUrl = 'https://api64.ipify.org?format=text';

  constructor(
    private http: HttpClient,
    private fb: FormBuilder,
    private store: Store<AppState>,
    private channelService: ChannelService,
    private deviceService: DeviceDetectorService,
    private toastr: ToastrService
  ) {
    this.working$ = this.store.select(
      (state) => state.channel.loading ?? false
    );

    this.channelNameFormGroup = this.fb.group({
      name: new FormControl('', [
        Validators.required,
        Validators.minLength(10),
        Validators.maxLength(200),
      ]),
      isUnique: new FormControl(false, Validators.requiredTrue),
    });

    this.acceptTermsFormGroup = this.fb.group({
      description: new FormControl('', [
        Validators.required,
        Validators.minLength(10),
        Validators.maxLength(500),
      ]),
      tagField: new FormControl(''),
      acceptTerms: new FormControl(false, Validators.requiredTrue),
    });
  }

  addTag() {
    this.acceptTermsFormGroup.get('tagField')?.setErrors(null);
    if (this.acceptTermsFormGroup.get('tagField')?.value === null) return;
    const fieldValue: string = this.acceptTermsFormGroup
      .get('tagField')
      ?.value.trim();
    if (fieldValue === '') return;
    if (fieldValue.includes(' '))
      return this.acceptTermsFormGroup.controls['tagField'].setErrors({
        invalidValue: true,
      });
    if ((this.tags.length ?? 0) >= 10)
      return this.acceptTermsFormGroup.controls['tagField'].setErrors({
        maxValuesAllowed: true,
      });
    this.tags = [...this.tags, fieldValue];
    this.acceptTermsFormGroup.get('tagField')?.setValue('');
    this.acceptTermsFormGroup.get('tagField')?.markAsPristine();
  }

  removeTag(tag: number) {
    this.tags.splice(tag, 1);
  }

  checkAvailability() {
    this.checkingAvailability = true;
    let name = this.channelNameFormGroup.get('name')?.value;
    this.channelService
      .checkAvailability(name)
      .then((res) => {
        if (res) {
          this.channelNameFormGroup.get('name')?.setErrors(null);
          this.channelNameFormGroup.get('isUnique')?.setValue(true);
          this.stepper.next();
        } else {
          this.channelNameFormGroup.get('name')?.setErrors({ nameTaken: true });
        }
        this.checkingAvailability = false;
      })
      .catch((error: any) => {
        this.channelNameFormGroup.get('name')?.setErrors({ failed: error });
        this.checkingAvailability = false;
      });
  }

  namingDialog() {
    this.dialog.open(NamingGuidelinesComponent);
  }

  channelTnCs() {
    this.dialog.open(ChannelTncComponent);
  }

  async createChannel() {
    if (this.channelNameFormGroup.get('name')?.invalid) return;
    if (!this.channelNameFormGroup.get('isUnique')) return;
    if (this.acceptTermsFormGroup.invalid) return;

    const deviceIp: any = await this.getIpAddress();
    if (!deviceIp) {
      this.toastr.error(
        'Failed to get device information. Please try again later.',
        'Error Creating Channel'
      );
      return;
    }

    const deviceInfo = await this.deviceDetails();

    var channel: any = {
      name: this.channelNameFormGroup.get('name')?.value,
      description: this.acceptTermsFormGroup.get('description')?.value,
      tags: this.tags,
      ip: deviceIp,
      machine: deviceInfo.machine,
    };

    this.store.dispatch(channelActions.createChannel({ channel }));
  }

  async deviceDetails() {
    const deviceInfo = this.deviceService.getDeviceInfo();
    return {
      machine: `${deviceInfo.device} ${deviceInfo.os} ${deviceInfo.os_version}`,
    };
  }

  async getIpAddress(): Promise<any> {
    return await axios.get(this.ipApiUrl).then((res) => {
      return res.data;
    });
  }

  goToCheckAvailability() {
    this.channelNameFormGroup.reset({ name: undefined, isUnique: false });
    this.stepper.previous();
  }
}
